import {
  AccountCircle as AccountCircleIcon,
  Category as CategoryIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  FitnessCenter as MicroPracticesIcon,
  Psychology as ModulesIcon,
  School as ProgramsIcon,
  Article as ResourcesIcon,
  Settings as SettingsIcon,
  People as UsersIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const drawerWidth = 280;

interface LayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  description: string;
  submenu?: MenuItem[];
}

const menuItems: MenuItem[] = [
  {
    text: "Dashboard",
    icon: <DashboardIcon />,
    path: "/",
    description: "Overview and metrics",
  },
  {
    text: "Programs",
    icon: <ProgramsIcon />,
    description: "Wellness programs",
    submenu: [
      {
        text: "Programs",
        icon: <ProgramsIcon />,
        path: "/programs",
        description: "Manage wellness programs",
      },
      {
        text: "Program Categories",
        icon: <CategoryIcon />,
        path: "/program-categories",
        description: "Manage program categories",
      },
    ],
  },
  {
    text: "Resources",
    icon: <ResourcesIcon />,
    description: "Wellness resources",
    submenu: [
      {
        text: "Resources",
        icon: <ResourcesIcon />,
        path: "/resources",
        description: "Manage wellness resources",
      },
      {
        text: "Resource Categories",
        icon: <CategoryIcon />,
        path: "/resource-categories",
        description: "Manage resource categories",
      },
    ],
  },
  {
    text: "Modules",
    icon: <ModulesIcon />,
    path: "/modules",
    description: "Manage wellness content",
  },
  {
    text: "Micro Practices",
    icon: <MicroPracticesIcon />,
    path: "/micro-practices",
    description: "Quick wellness activities",
  },
  {
    text: "Users",
    icon: <UsersIcon />,
    path: "/users",
    description: "User management",
  },
  {
    text: "Access Control",
    icon: <SecurityIcon />,
    description: "Roles and permissions",
    submenu: [
      {
        text: "Roles",
        icon: <SecurityIcon />,
        path: "/roles",
        description: "Manage user roles",
      },
      {
        text: "Permissions",
        icon: <SecurityIcon />,
        path: "/permissions",
        description: "Manage system permissions",
      },
    ],
  },
  // {
  //   text: "Settings",
  //   icon: <SettingsIcon />,
  //   path: "/settings",
  //   description: "App configuration",
  // },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { logout, user } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleNavigation = (path: string) => {
    if (path) {
      navigate(path);
      if (isMobile) {
        setMobileOpen(false);
      }
    }
  };

  const handleMenuToggle = (menuText: string) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuText)) {
      newExpanded.delete(menuText);
    } else {
      newExpanded.add(menuText);
    }
    setExpandedMenus(newExpanded);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header with Toggle */}
      <Paper
        elevation={0}
        sx={{
          p: sidebarCollapsed ? 2 : 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {!sidebarCollapsed && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              component='img'
              src='/logo.png'
              alt='Rise Strong Logo'
              sx={{
                height: 40,
                width: 40,
                mr: 2,
                borderRadius: 1,
              }}
            />
            <Box>
              <Typography variant='h6' fontWeight='bold' sx={{ color: "white" }}>
                Rise Strong
              </Typography>
              <Typography variant='body2' sx={{ color: "white", opacity: 0.9 }}>
                Admin Panel
              </Typography>
            </Box>
          </Box>
        )}
        <IconButton
          onClick={handleSidebarToggle}
          sx={{
            color: "white",
            ml: sidebarCollapsed ? 0 : "auto",
          }}
        >
          <ChevronLeftIcon
            sx={{
              transform: sidebarCollapsed ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease",
            }}
          />
        </IconButton>
      </Paper>

      {/* Navigation */}
      <Box sx={{ flexGrow: 1, py: 2 }}>
        <List sx={{ px: sidebarCollapsed ? 1 : 2 }}>
          {menuItems.map((item) => {
            const isSelected = location.pathname === item.path;
            const hasSubmenu = item.submenu && item.submenu.length > 0;
            const isExpanded = expandedMenus.has(item.text);
            const isSubmenuItemSelected = item.submenu?.some(
              (sub) => sub.path && location.pathname === sub.path.split("?")[0]
            );

            return (
              <React.Fragment key={item.text}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <Tooltip
                    title={sidebarCollapsed ? item.description : ""}
                    placement='right'
                  >
                    <ListItemButton
                      selected={isSelected || isSubmenuItemSelected}
                      onClick={() => {
                        if (hasSubmenu && !sidebarCollapsed) {
                          handleMenuToggle(item.text);
                        } else if (!hasSubmenu) {
                          handleNavigation(item.path || "");
                        }
                      }}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        px: sidebarCollapsed ? 1.5 : 2,
                        justifyContent: sidebarCollapsed
                          ? "center"
                          : "flex-start",
                        "&.Mui-selected": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1
                          ),
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.15
                            ),
                          },
                          "& .MuiListItemIcon-root": {
                            color: theme.palette.primary.main,
                          },
                          "& .MuiListItemText-primary": {
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                          },
                        },
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.05
                          ),
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: sidebarCollapsed ? "auto" : 40,
                          justifyContent: "center",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {!sidebarCollapsed && (
                        <>
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontSize: "0.95rem",
                              fontWeight:
                                isSelected || isSubmenuItemSelected ? 600 : 400,
                            }}
                          />
                          {hasSubmenu && (
                            <Box
                              sx={{
                                transform: isExpanded
                                  ? "rotate(90deg)"
                                  : "rotate(0deg)",
                                transition: "transform 0.2s",
                              }}
                            >
                              <ChevronRightIcon fontSize='small' />
                            </Box>
                          )}
                        </>
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
                {!sidebarCollapsed && hasSubmenu && isExpanded && (
                  <List sx={{ pl: 4, mb: 0.5 }}>
                    {item.submenu?.map((subItem) => {
                      const isSubSelected = subItem.path
                        ? location.pathname === subItem.path.split("?")[0]
                        : false;
                      return (
                        <ListItem key={subItem.text} disablePadding>
                          <ListItemButton
                            selected={isSubSelected}
                            onClick={() => handleNavigation(subItem.path || "")}
                            sx={{
                              borderRadius: 2,
                              mb: 0.5,
                              px: 2,
                              py: 0.5,
                              "&.Mui-selected": {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.1
                                ),
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.15
                                  ),
                                },
                                "& .MuiListItemIcon-root": {
                                  color: theme.palette.primary.main,
                                },
                                "& .MuiListItemText-primary": {
                                  color: theme.palette.primary.main,
                                  fontWeight: 600,
                                },
                              },
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.05
                                ),
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 32,
                                justifyContent: "center",
                              }}
                            >
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={subItem.text}
                              primaryTypographyProps={{
                                fontSize: "0.875rem",
                                fontWeight: isSubSelected ? 600 : 400,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* User Section */}
      <Paper
        elevation={0}
        sx={{
          m: 2,
          p: sidebarCollapsed ? 1 : 2,
          backgroundColor: alpha(theme.palette.grey[100], 0.5),
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
        }}
      >
        {sidebarCollapsed ? (
          // Collapsed view - just logout button
          <Tooltip title='Logout' placement='right'>
            <IconButton
              onClick={handleLogout}
              sx={{
                width: "100%",
                height: 40,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                backgroundColor: "white",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  borderColor: theme.palette.error.main,
                },
              }}
            >
              <LogoutIcon sx={{ color: "error.main" }} />
            </IconButton>
          </Tooltip>
        ) : (
          // Expanded view - full user info and logout button
          <>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Avatar
                sx={{ width: 32, height: 32, mr: 1.5, bgcolor: "primary.main" }}
              >
                <AccountCircleIcon />
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant='body2' fontWeight='600' noWrap>
                  {user?.email?.split("@")[0] || "Admin"}
                </Typography>
                <Typography variant='caption' color='textSecondary' noWrap>
                  Administrator
                </Typography>
              </Box>
            </Box>

            <Button
              fullWidth
              variant='outlined'
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              size='small'
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Sign Out
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );

  const currentDrawerWidth = sidebarCollapsed ? 80 : drawerWidth;

  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <Box
        component='nav'
        sx={{
          width: { md: currentDrawerWidth },
          flexShrink: 0,
          transition: "width 0.3s ease",
        }}
      >
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant='permanent'
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: currentDrawerWidth,
              borderRight: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              backgroundColor: alpha(theme.palette.grey[50], 0.3),
              transition: "width 0.3s ease",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component='main'
        sx={{
          flex: 1,
          p: { xs: 2, md: 3 },
          backgroundColor: alpha(theme.palette.grey[50], 0.3),
          minHeight: "100vh",
          transition: "all 0.3s ease",
          overflow: "hidden",
        }}
      >
        {/* Mobile Menu Button */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            mb: 2,
            position: "sticky",
            top: 16,
            zIndex: 1,
          }}
        >
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              backgroundColor: "white",
              boxShadow: `0 2px 8px ${alpha(theme.palette.grey[300], 0.3)}`,
              "&:hover": {
                backgroundColor: "grey.50",
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            p: 3,
            boxShadow: `0 1px 3px ${alpha(theme.palette.grey[300], 0.3)}`,
            border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
            minHeight: "calc(100vh - 24px)",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
